import React from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import {Trans, useTranslation} from 'react-i18next';
import {Controller, useFormContext} from 'react-hook-form';
import {EXTRA_FORM_NAMES, FormTypes} from 'utils/guestForm';
import {useSchema} from 'context/guest';
import {SessionStorage} from 'utils/storage';
import {STORAGE_KEYS} from 'utils/constants';
import {useChekinSDK} from 'context/ChekinSDK';
import {useReservation} from 'context/reservation';
import {useParams} from 'react-router-dom';
import {useGuest} from 'hooks';
import {
  useErrorModal,
  useModalControls,
  useScreenResize,
  useTimeoutRef,
} from 'hooks/common';
import {downloadFromLink, getDocumentDownloadByLanguage} from 'utils/common';
import {device} from 'styled/device';
import {fakeSignature} from './index';
import signIcon from 'assets/icons/sign-icon.svg';
import Modal from 'components/Modal';
import Button from 'components/common/Button';
import SignatureCanvas from 'components/common/SignatureCanvas';
import Link from 'components/common/Link';
import {Checkbox} from '@guestapp/ui';
import SignatureModal from './SignatureModal';
import {
  CheckboxContainer,
  AcceptWarning,
  DesktopSignArea,
  DesktopSignAreaContainer,
  DesktopSignatureImage,
  LinkInText,
  RepeatSignatureButton,
  SignButton,
  SubmitButton,
  Title,
  Wrapper,
  PleaseSignModal,
  WrapperWithButton,
  ErrorMessage,
  ContractTitleLine,
} from './styled';
import {ModalSheet} from 'components/common/ModalSheet/ModalSheet';
import fetcher from 'utils/fetcher';

type ContractType = {
  file: string;
};

export type SignBlockRef = {
  reset: () => void;
  set: (base64: string) => void;
  isEnabled: boolean;
  canvas?: HTMLCanvasElement;
};

export type SignBlockProps = {
  onSubmit: (signature: string) => void;
  isSignModalOpen: boolean;
  setIsSignModalOpen: (value: boolean) => void;
  showSignArea?: boolean;
  automatedSignature?: boolean;
  contracts: Array<ContractType>;
  isLoadingContracts?: boolean;
  isContractsError?: boolean;
  isRequired?: boolean;
};

const SignBlock = React.forwardRef<SignBlockRef, SignBlockProps>(
  (
    {
      onSubmit,
      isSignModalOpen,
      setIsSignModalOpen,
      showSignArea,
      automatedSignature,
      contracts,
      isContractsError,
      isLoadingContracts,
      isRequired,
    }: SignBlockProps,
    ref,
  ) => {
    const {t, i18n} = useTranslation();
    const signatureRef = React.useRef<ReactSignatureCanvas>(null);
    const {ErrorModal: ErrorModalContract, displayError} = useErrorModal();
    const {housing, housingId} = useReservation();
    const {id: guestId} = useParams<{id: string}>();
    const {guest} = useGuest({id: guestId});
    const {isGuestSchemaLoading, isGuestSchemaLoaded} = useSchema();

    const {reservationId, getPrivacyPolicy} = useChekinSDK();
    const isContractEnabled = housing?.is_contract_enabled;
    const [signature, setSignature] = React.useState<string>('');
    const [isSignatureEnabled, setIsSignatureEnabled] = React.useState(false);
    // const {IdGuestAppEvents} = useMixPanel();
    const [isSignatureRequired, setIsSignatureRequired] = React.useState(false);
    const [superHogTerms] = React.useState<boolean>(true);
    const [isSuperHogTermsModalOpen, setIsSuperHogTermsModalOpen] = React.useState(false);
    const [propertyHasSuperHog /*setPropertyHasSuperHog*/] = React.useState(false);
    const {isMatch: isMobile} = useScreenResize(device.tablet);
    const {control} = useFormContext<FormTypes>();
    const timeoutRef = useTimeoutRef();
    const [hasCustomPrivacyPolicy, setHasCustomPrivacyPolicy] =
      React.useState<boolean>(false);
    const [documentDownload, setDocumentDownload] = React.useState<string>('');

    const {
      isOpen: isSignatureModal,
      openModal: openSignatureModal,
      closeModal: closeSignatureModal,
    } = useModalControls();

    const handleOpenSignatureModal = React.useCallback(() => {
      setSignature('');
      setIsSignatureEnabled(true);
      openSignatureModal();
      setIsSignatureRequired(false);
      SessionStorage.remove(STORAGE_KEYS.personalSignatureFormData);
    }, [openSignatureModal]);

    const fetchSettingsCustomPrivacyPolicy = React.useCallback(async () => {
      if (!housingId) {
        return;
      }

      const data = await fetcher(getPrivacyPolicy, housingId);

      if (data) {
        const result = getDocumentDownloadByLanguage(data, i18n.language);
        setDocumentDownload(result);
        setHasCustomPrivacyPolicy(data.has_custom_privacy_policy);
      }

      if (data.error) {
        displayError(data.error);
      }
    }, [displayError, getPrivacyPolicy, housingId, i18n.language]);

    React.useEffect(() => {
      if (!housing) {
        return;
      }
      fetchSettingsCustomPrivacyPolicy();
    }, [housing, fetchSettingsCustomPrivacyPolicy]);

    React.useEffect(() => {
      if (isMobile) {
        SessionStorage.set(STORAGE_KEYS.personalSignatureFormData, signature);
      }
    }, [signature, isMobile]);

    React.useEffect(
      function preloadPersistedLocationSignature() {
        const storageSignature = SessionStorage.get<string>(
          STORAGE_KEYS.personalSignatureFormData,
        );
        const loadedSignature = guest?.id ? guest?.signature : storageSignature;
        if (
          !isSignatureModal &&
          loadedSignature &&
          reservationId &&
          isGuestSchemaLoaded
        ) {
          timeoutRef.current = setTimeout(() => {
            signatureRef.current?.fromDataURL(loadedSignature, {
              width: 319,
              height: 182,
            });

            setIsSignatureEnabled(true);
            setSignature(loadedSignature);
          }, 100); // improve
        }
      },
      [
        reservationId,
        isGuestSchemaLoaded,
        guest?.id,
        guest?.signature,
        isSignatureModal,
        timeoutRef,
      ],
    );

    const clearSignature = React.useCallback(() => {
      signatureRef.current?.clear();
      setSignature('');
      SessionStorage.remove(STORAGE_KEYS.personalSignatureFormData);
    }, []);

    const resetSignature = () => {
      clearSignature();
      setIsSignatureEnabled(false);
    };

    const putSignature = (base64: string) => {
      setIsSignatureEnabled(true);
      setSignature(base64);
    };

    const checkIsAutomatedSignature = (
      automatedSignature?: boolean,
      showSignArea?: boolean,
    ) => {
      if (!showSignArea) {
        return false;
      }
      return automatedSignature;
    };

    React.useImperativeHandle(ref, () => ({
      reset: resetSignature,
      set: putSignature,
      canvas: signatureRef.current?.getCanvas(),
      isEnabled: isSignatureEnabled,
    }));

    const saveSignature = React.useCallback(() => {
      const signature = signatureRef.current?.getCanvas()?.toDataURL();
      if (signature) setSignature(signature);
    }, []);

    const handleSignatureEnable = React.useCallback(() => {
      setIsSignatureEnabled(true);
    }, []);

    const handleSignModalClose = () => {
      setIsSignModalOpen(false);
      handleSignatureEnable();
    };

    const handleSuperHogTermsModalClose = () => {
      setIsSuperHogTermsModalOpen(false);
    };

    const viewContract = () => {
      const contract = contracts[0];

      if (isContractsError || !contract?.file) {
        displayError();
        return;
      }

      downloadFromLink(contract?.file);
    };

    const handleSubmit = async () => {
      const showSignModal = isSignatureEnabled && !Boolean(signature) && showSignArea;

      if (!showSignModal && !showSignArea && !automatedSignature) {
        onSubmit(fakeSignature);
        return;
      }

      if (!Boolean(signature) && showSignArea && !automatedSignature) {
        setIsSignatureRequired(!!isRequired);
        onSubmit(signature);
        return;
      }

      if (propertyHasSuperHog && !superHogTerms) {
        setIsSuperHogTermsModalOpen(true);
        return;
      }

      if (!showSignModal && (Boolean(signature) || automatedSignature)) {
        onSubmit(signature);
        return;
      }
    };

    const signatureArea = React.useMemo(() => {
      return (
        <SignatureCanvas
          ref={signatureRef}
          onClear={clearSignature}
          onEnable={handleSignatureEnable}
          enabled={isSignatureEnabled}
          hasSignature={Boolean(signature)}
          onEnd={saveSignature}
        />
      );
    }, [
      signatureRef,
      clearSignature,
      handleSignatureEnable,
      isSignatureEnabled,
      signature,
      saveSignature,
    ]);

    return (
      <>
        <ErrorModalContract
          title={t('contract_not_available')}
          message={t('contract_not_available_description') as string}
        />
        <Modal open={isSignModalOpen} onClose={handleSignModalClose}>
          <PleaseSignModal>
            <img src={signIcon} alt="" width="84" height="84" />
            <div className="title">{t('you_have_to_sign')}</div>
            <div className="text">
              {isContractEnabled ? (
                <Trans i18nKey="please_sign_and_accept_terms_and_contract">
                  Please <b>sign</b> inside the box. You can use your finger.
                </Trans>
              ) : (
                <Trans i18nKey="please_sign_and_accept_terms">
                  Please <b>sign</b> inside the box. You can use your finger.
                </Trans>
              )}
            </div>
            <Button label={t('sign_now')} onClick={handleSignModalClose} />
          </PleaseSignModal>
        </Modal>
        <Modal open={isSuperHogTermsModalOpen} onClose={handleSuperHogTermsModalClose}>
          <PleaseSignModal>
            <img src={signIcon} alt="" width="84" height="84" />
            <div className="title">{t('accept_superhog_terms')}</div>
            <div className="text">
              <Trans i18nKey="accept_superhog_terms_description">
                In order to continue you must accept the{' '}
                <b>Superhog data verification agreement.</b>
              </Trans>
            </div>
            <Button label={t('ok')} onClick={handleSuperHogTermsModalClose} />
          </PleaseSignModal>
        </Modal>
        <WrapperWithButton>
          <Wrapper>
            {showSignArea && (
              <>
                {/* {!isMobile && (
                  <>
                    {isLoadingContracts && (
                      <ContractBlock>{t('loading_contract')}</ContractBlock>
                    )}
                    {isContractEnabled && !isLoadingContracts && (
                      <ContractBlock data-testid="contract-btn" onClick={viewContract}>
                        <ContractIcon name="search" />
                        {t('view_contract')}
                      </ContractBlock>
                    )}
                  </>
                )} */}
                {!automatedSignature && (
                  // <SignatureCanvas
                  //   ref={signatureRef}
                  //   onClear={clearSignature}
                  //   onEnable={handleSignatureEnable}
                  //   enabled={isSignatureEnabled}
                  //   hasSignature={Boolean(signature)}
                  //   onEnd={saveSignature}
                  // />

                  <>
                    <ContractTitleLine>
                      <Title>{t('signature')}</Title>
                    </ContractTitleLine>
                    <DesktopSignAreaContainer
                      disabled={isGuestSchemaLoading}
                      onClick={handleOpenSignatureModal}
                      empty={Boolean(signature) && !isSignatureModal}
                      isRequire={isSignatureRequired}
                    >
                      {!signature && !isSignatureModal && (
                        <DesktopSignArea>
                          {!isGuestSchemaLoading && (
                            <SignButton>
                              {isMobile ? t('tap_here_to_sign') : t('click_here_to_sign')}
                            </SignButton>
                          )}
                        </DesktopSignArea>
                      )}
                      {signature && !isSignatureModal && (
                        <DesktopSignatureImage src={signature} />
                      )}
                    </DesktopSignAreaContainer>

                    {signature && !isSignatureModal && (
                      <RepeatSignatureButton onClick={handleOpenSignatureModal}>
                        {t('repeat_signature')}
                      </RepeatSignatureButton>
                    )}
                  </>
                )}
              </>
            )}
            {isSignatureRequired && (
              <ErrorMessage>{t('the_signature_is_required')}</ErrorMessage>
            )}
            {isContractEnabled && !isLoadingContracts && (
              <>
                <ContractTitleLine>
                  <Title>{t('contract')}</Title>
                </ContractTitleLine>
                <CheckboxContainer>
                  <Controller
                    name={EXTRA_FORM_NAMES.sendCopyContractEmail}
                    control={control}
                    render={({field, fieldState: {error}}) => (
                      <Checkbox
                        label={<Trans i18nKey="send_copy_contract_email" />}
                        error={error?.message}
                        {...field}
                      />
                    )}
                  />
                </CheckboxContainer>
              </>
            )}
            <AcceptWarning>
              {/* {!showSignArea && <>{t('by_clicking_next_you_accept_the')} </>} */}
              {!isContractEnabled &&
                !checkIsAutomatedSignature(automatedSignature, showSignArea) && (
                  <Trans i18nKey="by_clicking_next_you_accept_the">
                    By clicking <b>‘Sign form’</b> you accept to sign the form with your
                    name and the Privacy Policy
                  </Trans>
                )}
              {!isContractEnabled &&
                checkIsAutomatedSignature(automatedSignature, showSignArea) && (
                  <Trans i18nKey="by_clicking_signt_now_you_accept_the">
                    By clicking <b>‘Sign now’ </b>you accept to sign the form with your
                    name, the short term
                  </Trans>
                )}
              {isContractEnabled && (
                <>
                  {!checkIsAutomatedSignature(automatedSignature, showSignArea) ? (
                    <Trans i18nKey="by_clicking_next_you_accept_the">
                      By clicking <b>‘Sign form’</b> you accept to sign the form with your
                      name and the
                    </Trans>
                  ) : (
                    <Trans i18nKey="by_clicking_signt_now_you_accept_the">
                      By clicking <b>‘Sign now’ </b>you accept to sign the form with your
                      name, the short term
                    </Trans>
                  )}

                  <LinkInText data-testid="contract-btn" onClick={viewContract}>
                    {!checkIsAutomatedSignature(automatedSignature, showSignArea)
                      ? t('contract')
                      : t('contract')}
                  </LinkInText>
                </>
              )}
              {/* {isContractEnabled && !isLoadingContracts &&  original condition*/}
              {isContractEnabled && ` ${t('and_you_accept_our')} `}

              {hasCustomPrivacyPolicy ? (
                <Link download href={documentDownload}>
                  {t('privacy_policy')}
                </Link>
              ) : (
                <Link target="_blank" rel="noreferrer" href={t('privacy_policy_link')}>
                  {t('privacy_policy')}
                </Link>
              )}
            </AcceptWarning>
            <SubmitButton>
              <Button
                primary
                data-testid="submit-btn"
                onClick={handleSubmit}
                // label={showSignArea && automatedSignature ? t('sign_form') : t('next')}
                label={t('sign_form')}
                type="button"
                // disabled={!formState.isValid}
                disabled={false}
                className="submit-btn"
              />
            </SubmitButton>
          </Wrapper>
        </WrapperWithButton>
        <ModalSheet
          isOpenModalSheet={isSignatureModal}
          handleCloseModal={closeSignatureModal}
          maxWidth={'900px'}
        >
          <SignatureModal
            onClose={closeSignatureModal}
            signatureArea={signatureArea}
            signature={signatureRef.current}
            onChange={setSignature}
            hasSignature={Boolean(signature)}
          />
        </ModalSheet>
      </>
    );
  },
);

export {SignBlock};
